import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Grid, Divider, IconButton, Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MapIcon from '@mui/icons-material/Map';

const ACTIVE_STAGES = ['identified', 'qualified', 'demo', 'sandbox', 'proposal', 'negotiation', 'pilot'];

function formatSEK(val) {
  if (!val) return '0 KSEK';
  return Number(val).toLocaleString('sv-SE') + ' KSEK';
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date(new Date().toDateString());
}

export default function Affarer() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    companies: 0,
    activeLeads: 0,
    pipeline: 0,
    wonLeads: 0,
    overdueFollowUps: 0,
    products: 0,
    plingThisMonth: 0,
    klingThisMonth: 0,
  });

  useEffect(() => {
    async function loadStats() {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const [compSnap, leadsSnap, productsSnap, plingSnap] = await Promise.all([
        getDocs(collection(db, 'crmCompanies')),
        getDocs(collection(db, 'crmLeads')),
        getDocs(collection(db, 'products')),
        getDocs(query(collection(db, 'plingKlang'), orderBy('createdAt', 'desc'))),
      ]);

      const leads = leadsSnap.docs.map(d => d.data());
      const activeLeads = leads.filter(l => ACTIVE_STAGES.includes(l.status));
      const pipeline = activeLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0);
      const wonLeads = leads.filter(l => l.status === 'won').length;
      const overdueFollowUps = activeLeads.filter(l =>
        l.nextFollowUp && isOverdue(l.nextFollowUp)
      ).length;

      const plings = plingSnap.docs.map(d => d.data());
      const plingThisMonth = plings.filter(p =>
        p.type === 'pling' && p.createdAt?.toDate?.() >= monthStart
      ).length;
      const klingThisMonth = plings.filter(p =>
        p.type === 'kling-klang' && p.createdAt?.toDate?.() >= monthStart
      ).length;

      setStats({
        companies: compSnap.size,
        activeLeads: activeLeads.length,
        pipeline,
        wonLeads,
        overdueFollowUps,
        products: productsSnap.size,
        plingThisMonth,
        klingThisMonth,
      });
    }
    loadStats();
  }, []);

  const sections = [
    {
      title: 'Kunder',
      desc: 'Företagsregister med kontakter, kundstatus och referensuppdrag',
      icon: <BusinessIcon sx={{ fontSize: 32, color: '#FF6B00' }} />,
      path: '/crm/companies',
      badge: `${stats.companies} företag`,
      badgeColor: '#1a1a1a',
      accentColor: '#FF6B00',
    },
    {
      title: 'Pipeline',
      desc: 'Leads från identifierat till vunnet — med kanban och listvy',
      icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#1565C0' }} />,
      path: '/crm/leads',
      badge: `${stats.activeLeads} aktiva`,
      badgeColor: '#1565C0',
      subStat: stats.pipeline ? formatSEK(stats.pipeline) + ' i aktiv pipeline' : null,
      accentColor: '#1565C0',
      alert: stats.overdueFollowUps > 0 ? `${stats.overdueFollowUps} förfallna uppföljningar` : null,
    },
    {
      title: 'Produktkatalog',
      desc: 'Hantera produkter som FLATBOX och kommande erbjudanden',
      icon: <StorefrontIcon sx={{ fontSize: 32, color: '#00897B' }} />,
      path: '/produkter',
      badge: `${stats.products} produkter`,
      badgeColor: '#00897B',
      accentColor: '#00897B',
    },
    {
      title: 'PLING & KLING-KLANG',
      desc: 'AI-genererade interna nyheter om nya leads och vunna affärer',
      icon: <NotificationsActiveIcon sx={{ fontSize: 32, color: '#6A1B9A' }} />,
      path: '/crm/pling-klang',
      accentColor: '#6A1B9A',
      subStat: (stats.plingThisMonth + stats.klingThisMonth) > 0
        ? `${stats.plingThisMonth} PLING + ${stats.klingThisMonth} KLING-KLANG denna månad`
        : 'Inga denna månad',
    },
    {
      title: 'Statistik & KPI:er',
      desc: 'Mätare, trender och säljinsikter — för dig som vill vinna mer',
      icon: <BarChartIcon sx={{ fontSize: 32, color: '#E65100' }} />,
      path: '/affarer/statistik',
      badgeColor: '#E65100',
      accentColor: '#E65100',
      subStat: stats.wonLeads > 0 ? `${stats.wonLeads} vunna affärer totalt` : null,
    },
    {
      title: 'Kalender',
      desc: 'Uppföljningar och aktiviteter i kalendervy — filtrerbar per säljare och AO',
      icon: <CalendarMonthIcon sx={{ fontSize: 32, color: '#0277BD' }} />,
      path: '/affarer/kalender',
      accentColor: '#0277BD',
      subStat: stats.overdueFollowUps > 0 ? `${stats.overdueFollowUps} förfallna uppföljningar` : null,
    },
    {
      title: 'Karta',
      desc: 'Geografisk utbredning av leads och vunna affärer — filtrerbar på AO och produkt',
      icon: <MapIcon sx={{ fontSize: 32, color: '#2E7D32' }} />,
      path: '/affarer/karta',
      accentColor: '#2E7D32',
    },
  ];

  const monthName = new Date().toLocaleString('sv-SE', { month: 'long' });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Affärer</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Kunder, pipeline, produkter och säljstöd
            </Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Summary row */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { label: 'Kunder', value: stats.companies, icon: <BusinessIcon sx={{ color: '#FF6B00' }} />, color: '#FF6B00' },
            { label: 'Aktiva leads', value: stats.activeLeads, icon: <TrendingUpIcon sx={{ color: '#1565C0' }} />, color: '#1565C0' },
            { label: 'Pipeline-värde', value: formatSEK(stats.pipeline), icon: <EmojiEventsIcon sx={{ color: '#2E7D32' }} />, color: '#2E7D32' },
            { label: 'Vunna affärer', value: stats.wonLeads, icon: <GroupsIcon sx={{ color: '#6A1B9A' }} />, color: '#6A1B9A' },
          ].map((s, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Paper sx={{ p: 2.5, borderTop: `3px solid ${s.color}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  {s.icon}
                  <Typography variant="h6" fontWeight="bold" sx={{ color: s.color }}>
                    {s.value}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* PLING stats for current month */}
        {(stats.plingThisMonth > 0 || stats.klingThisMonth > 0) && (
          <Paper sx={{ p: 2.5, mb: 3, bgcolor: '#1a1a1a', color: 'white',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <NotificationsActiveIcon sx={{ color: '#FF6B00' }} />
            <Typography variant="subtitle2" fontWeight="bold">
              {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
            </Typography>
            <Chip label={`${stats.plingThisMonth} PLING 🔔`} size="small"
              sx={{ bgcolor: '#FF6B00', color: 'white', fontWeight: 700 }} />
            <Chip label={`${stats.klingThisMonth} KLING-KLANG 🏆`} size="small"
              sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 700 }} />
            {stats.overdueFollowUps > 0 && (
              <Chip
                icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                label={`${stats.overdueFollowUps} förfallna uppföljningar`}
                size="small"
                onClick={() => navigate('/crm/leads')}
                sx={{ bgcolor: '#C62828', color: 'white', fontWeight: 700, cursor: 'pointer' }} />
            )}
          </Paper>
        )}

        {/* Section cards */}
        <Grid container spacing={3}>
          {sections.map((sec, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Paper onClick={() => navigate(sec.path)} sx={{
                p: 3, cursor: 'pointer', height: '100%',
                transition: 'all 0.15s',
                borderLeft: `5px solid transparent`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                  borderLeft: `5px solid ${sec.accentColor}`,
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start',
                  justifyContent: 'space-between', mb: 2 }}>
                  {sec.icon}
                  {sec.badge && (
                    <Chip label={sec.badge} size="small"
                      sx={{ fontSize: '0.65rem', height: 20,
                        bgcolor: (sec.badgeColor || sec.accentColor) + '18',
                        color: sec.badgeColor || sec.accentColor }} />
                  )}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {sec.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sec.desc}
                </Typography>
                {sec.subStat && (
                  <Typography variant="caption"
                    sx={{ color: sec.accentColor, mt: 1, display: 'block', fontWeight: 500 }}>
                    {sec.subStat}
                  </Typography>
                )}
                {sec.alert && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <WarningAmberIcon sx={{ fontSize: 14, color: '#C62828' }} />
                    <Typography variant="caption" sx={{ color: '#C62828', fontWeight: 600 }}>
                      {sec.alert}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
